// ScenarioIQ Context for State Management

import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Scenario,
  ScenarioResult,
  ScenarioSummary,
  UserProductFamilies,
  LoadingState,
  ScenarioFormState,
  ProductFamilySelection,
  ExcludedOpportunity,
  PayerProfitabilityItem,
  FeeScheduleGap,
  RebateDetail
} from '../types';

// State interface
interface ScenarioState {
  // Current user and selection
  currentUser: string | null;
  currentCategory: string | null;
  userProductFamilies: UserProductFamilies | null;
  selectedExcludedOpportunities: ExcludedOpportunity[] | null;
  
  // Generator form cache
  generatorState: {
    form: Partial<ScenarioFormState>;
    selectedFamilies: Record<string, ProductFamilySelection>;
    userName: string | null;
    useCurrentOnly: boolean;
    // Last-used generation algorithm, remembered across sessions. Absent = first
    // login → defaults to distribution-based.
    preferredGenerationMode?: 'payer' | 'distribution';
  };

  // Scenarios
  scenarios: Scenario[];
  scenarioResults: ScenarioResult[];
  scenarioSummary: ScenarioSummary[];
  // Background incremental generation status
  generationStatus?: {
    totalCategories: number;
    completedCategories: number;
    inProgress: boolean;
  };
  
  // UI state
  loading: LoadingState;
  selectedScenarios: number[];

  // Alternate scenario tracking (persists across page navigation within session)
  pinnedAlternateScenarioIds: number[];
  // Maps alternate scenario ID → parent scenario ID it was derived from
  alternateParentMap: Record<number, number>;

  // Excluded payer volume info per category (from greedy generation)
  excludedPayerInfo: Record<string, { excludedPayers: string[]; excludedPayerVolume: number }>;

  // PF IDs missing a fee schedule, per category (weighted NCR falls back to Medicare for these)
  missingFeeSchedulePfIds: Record<string, string[]>;

  // Per-PF partial fee-schedule gaps, per category: pfId -> gap. The PF has a schedule but is
  // missing rates for some eligible payers, so their volume uses Medicare (weighted NCR is
  // approximate). Distinct from missingFeeSchedulePfIds, which is whole-PF-missing.
  feeScheduleGaps: Record<string, Record<string, FeeScheduleGap>>;

  // Per-payer profitability (fee-schedule reimbursement based), keyed by scenario_id.
  // Persisted like results/payer assignments — a snapshot that only refreshes on a new
  // generation. Fee schedules change ~quarterly, so brief staleness is acceptable.
  payerProfitabilityByScenarioId: Record<number, Record<string, PayerProfitabilityItem>>;

  // Contract-opportunity + tier rebate breakdown rows from the generation response.
  // Session-only (NOT persisted to localStorage — can be thousands of rows); after a
  // page refresh the drill-down prompts the user to regenerate.
  rebateDetails: RebateDetail[];

  token: string;

  currentOrg: string | null;
}

// Action types
type ScenarioAction =
  | { type: 'SET_LOADING'; payload: { isLoading: boolean; error?: string } }
  | { type: 'SET_CURRENT_USER'; payload: string }
  | { type: 'SET_CURRENT_CATEGORY'; payload: string }
  | { type: 'SET_USER_PRODUCT_FAMILIES'; payload: UserProductFamilies }
  | { type: 'SET_SCENARIOS'; payload: Scenario[] }
  | { type: 'SET_SCENARIO_RESULTS'; payload: { results: ScenarioResult[]; summary: ScenarioSummary[] } }
  | { type: 'SET_GENERATOR_STATE'; payload: Partial<ScenarioState['generatorState']> }
  | { type: 'ADD_SCENARIO'; payload: Scenario }
  | { type: 'REMOVE_SCENARIO'; payload: number }
  | { type: 'CLEAR_SCENARIOS' }
  | { type: 'SET_SELECTED_SCENARIOS'; payload: number[] }
  | { type: 'TOGGLE_SCENARIO_SELECTION'; payload: number }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CURRENT_ORG'; payload: string }
  | { type: 'APPEND_SCENARIO_RESULTS'; payload: { results: ScenarioResult[]; summary: ScenarioSummary[] } }
  | { type: 'INIT_GENERATION_SESSION'; payload: { totalCategories: number } }
  | { type: 'INCREMENT_GENERATION_COMPLETED' }
  | { type: 'END_GENERATION_SESSION' }
  | { type: 'SET_PINNED_ALTERNATE_SCENARIO_IDS'; payload: number[] }
  | { type: 'MERGE_ALTERNATE_PARENT_MAP'; payload: Record<number, number> }
  | { type: 'SET_EXCLUDED_PAYER_INFO'; payload: { category: string; excludedPayers: string[]; excludedPayerVolume: number } }
  | { type: 'SET_MISSING_FEE_SCHEDULE_PF_IDS'; payload: { category: string; pfIds: string[] } }
  | { type: 'SET_FEE_SCHEDULE_GAPS'; payload: { category: string; gaps: Record<string, FeeScheduleGap> } }
  | { type: 'MERGE_PAYER_PROFITABILITY'; payload: Record<number, Record<string, PayerProfitabilityItem>> }
  | { type: 'MERGE_REBATE_DETAILS'; payload: RebateDetail[] };

// Initial state
const initialState: ScenarioState = {
  currentUser: null,
  currentCategory: null,
  userProductFamilies: null,
  generatorState: {
    form: {},
    selectedFamilies: {},
    userName: null,
    useCurrentOnly: false,
  },
  scenarios: [],
  scenarioResults: [],
  scenarioSummary: [],
  loading: { isLoading: false },
  selectedScenarios: [],
  pinnedAlternateScenarioIds: [],
  alternateParentMap: {},
  excludedPayerInfo: {},
  missingFeeSchedulePfIds: {},
  feeScheduleGaps: {},
  payerProfitabilityByScenarioId: {},
  rebateDetails: [],
  token: '',
  currentOrg: null,
  generationStatus: undefined,
  selectedExcludedOpportunities: null,
};

// Reducer function
const scenarioReducer = (state: ScenarioState, action: ScenarioAction): ScenarioState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_GENERATOR_STATE':
      return {
        ...state,
        generatorState: {
          ...state.generatorState,
          ...action.payload,
        },
      };

    case 'SET_CURRENT_USER':
      return {
        ...state,
        currentUser: action.payload,
      };

    case 'SET_CURRENT_ORG':
      return {
        ...state,
        currentOrg: action.payload,
      };

    case 'SET_CURRENT_CATEGORY':
      return {
        ...state,
        currentCategory: action.payload,
      };

    case 'SET_USER_PRODUCT_FAMILIES':
      return {
        ...state,
        userProductFamilies: action.payload,
      };

    case 'SET_SCENARIOS':
      return {
        ...state,
        scenarios: action.payload,
        selectedScenarios: [], // Clear selection when new scenarios are loaded
      };

    case 'SET_SCENARIO_RESULTS':
      return {
        ...state,
        scenarioResults: action.payload.results,
        scenarioSummary: action.payload.summary,
        pinnedAlternateScenarioIds: [],
        alternateParentMap: {},
        excludedPayerInfo: {},
        missingFeeSchedulePfIds: {},
        feeScheduleGaps: {},
        // New generation invalidates the profitability snapshot.
        payerProfitabilityByScenarioId: {},
        // New generation invalidates the rebate drill-down snapshot.
        rebateDetails: [],
      };

    case 'APPEND_SCENARIO_RESULTS': {
      // Merge results avoiding duplicates (by scenario_id + product_family_id)
      const existingMap = new Map<string, ScenarioResult>();
      state.scenarioResults.forEach(r => existingMap.set(`${r.scenario_id}:${r.product_family_id}`, r));
      action.payload.results.forEach(r => {
        existingMap.set(`${r.scenario_id}:${r.product_family_id}`, r);
      });
      const mergedResults = Array.from(existingMap.values());
      // Merge summaries (replace by scenario_id + category to preserve per-category payer_assignments)
      const summaryMap = new Map<string, ScenarioSummary>();
      state.scenarioSummary.forEach(s => summaryMap.set(`${s.scenario_id}:${s.category ?? ''}`, s));
      action.payload.summary.forEach(s => summaryMap.set(`${s.scenario_id}:${s.category ?? ''}`, s));
      const mergedSummary = Array.from(summaryMap.values());
      return {
        ...state,
        scenarioResults: mergedResults,
        scenarioSummary: mergedSummary,
      };
    }

    case 'INIT_GENERATION_SESSION':
      return {
        ...state,
        generationStatus: {
          totalCategories: action.payload.totalCategories,
          completedCategories: 0,
          inProgress: true,
        },
      };

    case 'INCREMENT_GENERATION_COMPLETED':
      return {
        ...state,
        generationStatus: state.generationStatus
          ? {
              ...state.generationStatus,
              completedCategories: Math.min(
                state.generationStatus.completedCategories + 1,
                state.generationStatus.totalCategories
              ),
            }
          : state.generationStatus,
      };

    case 'END_GENERATION_SESSION':
      return {
        ...state,
        generationStatus: state.generationStatus
          ? { ...state.generationStatus, inProgress: false }
          : state.generationStatus,
      };

    case 'ADD_SCENARIO':
      return {
        ...state,
        scenarios: [...state.scenarios, action.payload],
      };

    case 'REMOVE_SCENARIO':
      return {
        ...state,
        scenarios: state.scenarios.filter(s => s.scenario_id !== action.payload),
        selectedScenarios: state.selectedScenarios.filter(id => id !== action.payload),
      };

    case 'CLEAR_SCENARIOS':
      return {
        ...state,
        scenarios: [],
        scenarioResults: [],
        scenarioSummary: [],
        selectedScenarios: [],
        payerProfitabilityByScenarioId: {},
        rebateDetails: [],
        // Also clear generator state when explicitly clearing scenarios
        generatorState: {
          form: {},
          selectedFamilies: {},
          userName: null,
          useCurrentOnly: false,
        },
      };

    case 'SET_SELECTED_SCENARIOS':
      return {
        ...state,
        selectedScenarios: action.payload,
      };

    case 'TOGGLE_SCENARIO_SELECTION':
      const scenarioId = action.payload;
      const isSelected = state.selectedScenarios.includes(scenarioId);
      return {
        ...state,
        selectedScenarios: isSelected
          ? state.selectedScenarios.filter(id => id !== scenarioId)
          : [...state.selectedScenarios, scenarioId],
      };

    case 'SET_PINNED_ALTERNATE_SCENARIO_IDS':
      return {
        ...state,
        pinnedAlternateScenarioIds: action.payload,
      };

    case 'MERGE_ALTERNATE_PARENT_MAP':
      return {
        ...state,
        alternateParentMap: { ...state.alternateParentMap, ...action.payload },
      };

    case 'SET_EXCLUDED_PAYER_INFO':
      return {
        ...state,
        excludedPayerInfo: {
          ...state.excludedPayerInfo,
          [action.payload.category]: {
            excludedPayers: action.payload.excludedPayers,
            excludedPayerVolume: action.payload.excludedPayerVolume,
          },
        },
      };

    case 'SET_MISSING_FEE_SCHEDULE_PF_IDS':
      return {
        ...state,
        missingFeeSchedulePfIds: {
          ...state.missingFeeSchedulePfIds,
          [action.payload.category]: action.payload.pfIds,
        },
      };

    case 'SET_FEE_SCHEDULE_GAPS':
      return {
        ...state,
        feeScheduleGaps: {
          ...state.feeScheduleGaps,
          [action.payload.category]: action.payload.gaps,
        },
      };

    case 'MERGE_PAYER_PROFITABILITY':
      return {
        ...state,
        payerProfitabilityByScenarioId: {
          ...state.payerProfitabilityByScenarioId,
          ...action.payload,
        },
      };

    case 'MERGE_REBATE_DETAILS': {
      // Replace-by-key so re-running a category refreshes its rows instead of appending.
      // Relies on contract_opportunities_id being stable across runs, which is why the
      // backend strips the SP's placeholder ids.
      const detailKey = (d: RebateDetail) =>
        `${d.scenario_id}:${d.product_family_id}:${d.rebate_source}:${d.contract_opportunities_id ?? ''}:${d.tier ?? ''}:${d.paid_on ?? ''}:${d.paid_as_value ?? ''}`;
      const merged = new Map<string, RebateDetail>();
      (state.rebateDetails ?? []).forEach(d => merged.set(detailKey(d), d));
      action.payload.forEach(d => merged.set(detailKey(d), d));
      return {
        ...state,
        rebateDetails: Array.from(merged.values()),
      };
    }

    case 'CLEAR_ALL':
      return initialState;

    default:
      return state;
  }
};

// Context
const ScenarioContext = createContext<{
  state: ScenarioState;
  dispatch: React.Dispatch<ScenarioAction>;
} | null>(null);

// Provider component
interface ScenarioProviderProps {
  children: ReactNode;
}

const LOCAL_STORAGE_KEY = 'scenarioState';

// Byte budget for the rebate rows in localStorage. Size, not row count, is what risks a
// QuotaExceededError. Trimmed rows measure ~500 bytes, so this holds ~5,000 — enough for 25
// scenarios against a 30-tier contract — and still leaves about half a 5 M-char origin quota
// for everything else. Over budget the rows are dropped and the drill-down asks for a rerun.
const MAX_PERSISTED_REBATE_BYTES = 2_500_000;

// Listed explicitly so a field added to the model later cannot silently bloat storage.
const trimRebateDetailForStorage = (d: RebateDetail): RebateDetail => ({
  scenario_id: d.scenario_id,
  logged_in_user: d.logged_in_user,
  product_family_id: d.product_family_id,
  product_family_name: d.product_family_name,
  rebate_source: d.rebate_source,
  contract_opportunities_id: d.contract_opportunities_id ?? null,
  has_contract: d.has_contract,
  opportunity: d.opportunity ?? null,
  paid_on: d.paid_on ?? null,
  trigger_type: d.trigger_type ?? null,
  unit_of_measure: d.unit_of_measure ?? null,
  tier: d.tier ?? null,
  min_calc: d.min_calc ?? null,
  secondary_min_calc: d.secondary_min_calc ?? null,
  total_units: d.total_units ?? null,
  secondary_total_units: d.secondary_total_units ?? null,
  paid_as_value: d.paid_as_value ?? null,
  rebate_earned: d.rebate_earned ?? null,
  exclusion_reason: d.exclusion_reason ?? null,
});

export const ScenarioProvider: React.FC<ScenarioProviderProps> = ({ children }) => {
  const { accounts } = useMsal();
  
  // Try to load saved state from localStorage on init
  let savedState: ScenarioState | null = null;
  try {
    const savedStateString = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedStateString) {
      savedState = JSON.parse(savedStateString);
    }
  } catch (error) {
    console.warn('Failed to parse saved scenario state from localStorage:', error);
  }

  const [state, dispatch] = React.useReducer(
    scenarioReducer,
    savedState ? { ...initialState, ...savedState } : initialState
  );

  // Set currentUser from MSAL accounts when available
  React.useEffect(() => {
    if (accounts.length > 0 && accounts[0].username && !state.currentUser) {
      dispatch({
        type: 'SET_CURRENT_USER',
        payload: accounts[0].username
      });
    }
  }, [accounts, state.currentUser]);

  // Save state to localStorage when key parts change
  React.useEffect(() => {
    try {
      const trimmed = (state.rebateDetails ?? []).map(trimRebateDetailForStorage);
      const trimmedRebateDetails =
        JSON.stringify(trimmed).length <= MAX_PERSISTED_REBATE_BYTES ? trimmed : [];
      const stateToSave = {
        ...state,
        // Save the parts you want persisted:
        scenarioResults: state.scenarioResults,
        scenarioSummary: state.scenarioSummary,
        currentUser: state.currentUser,
        currentCategory: state.currentCategory,
        userProductFamilies: state.userProductFamilies,
        scenarios: state.scenarios,
        currentOrg: state.currentOrg,
        generatorState: state.generatorState,
        pinnedAlternateScenarioIds: state.pinnedAlternateScenarioIds,
        alternateParentMap: state.alternateParentMap,
        payerProfitabilityByScenarioId: state.payerProfitabilityByScenarioId,
        // Persisted trimmed, so the breakdown survives a page reload.
        rebateDetails: trimmedRebateDetails,
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      // A quota failure would otherwise lose every persisted key. Retry without the rebate
      // rows so results, scenarios and generator state survive.
      try {
        const savedStateString = localStorage.getItem(LOCAL_STORAGE_KEY);
        const fallback = savedStateString ? JSON.parse(savedStateString) : {};
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({ ...fallback, ...state, rebateDetails: [] })
        );
      } catch {
        console.warn('Failed to save scenario state to localStorage:', error);
      }
    }
  }, [
    state.rebateDetails,
    state.scenarioResults,
    state.scenarioSummary,
    state.currentUser,
    state.currentCategory,
    state.userProductFamilies,
    state.scenarios,
    state.currentOrg,
    state.generatorState,
    state.pinnedAlternateScenarioIds,
    state.alternateParentMap,
    state.payerProfitabilityByScenarioId,
  ]);

  return (
    <ScenarioContext.Provider value={{ state, dispatch }}>
      {children}
    </ScenarioContext.Provider>
  );
};


// Custom hook to use the context
export const useScenarioContext = () => {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenarioContext must be used within a ScenarioProvider');
  }
  return context;
};

// Action creators (helper functions)
export const scenarioActions = {
  setLoading: (isLoading: boolean, error?: string) => ({
    type: 'SET_LOADING' as const,
    payload: { isLoading, error },
  }),

  setCurrentUser: (userId: string) => ({
    type: 'SET_CURRENT_USER' as const,
    payload: userId,
  }),

  setCurrentOrg: (orgName: string) => ({
    type: 'SET_CURRENT_ORG' as const,
    payload: orgName,
  }),

  setGeneratorState: (newState: Partial<ScenarioState['generatorState']>) => ({
    type: 'SET_GENERATOR_STATE' as const,
    payload: newState,
  }),

  setCurrentCategory: (category: string) => ({
    type: 'SET_CURRENT_CATEGORY' as const,
    payload: category,
  }),

  setUserProductFamilies: (userProductFamilies: UserProductFamilies) => ({
    type: 'SET_USER_PRODUCT_FAMILIES' as const,
    payload: userProductFamilies,
  }),

  setScenarios: (scenarios: Scenario[]) => ({
    type: 'SET_SCENARIOS' as const,
    payload: scenarios,
  }),

  setScenarioResults: (results: ScenarioResult[], summary: ScenarioSummary[]) => ({
    type: 'SET_SCENARIO_RESULTS' as const,
    payload: { results, summary },
  }),

  appendScenarioResults: (results: ScenarioResult[], summary: ScenarioSummary[]) => ({
    type: 'APPEND_SCENARIO_RESULTS' as const,
    payload: { results, summary },
  }),

  addScenario: (scenario: Scenario) => ({
    type: 'ADD_SCENARIO' as const,
    payload: scenario,
  }),

  removeScenario: (scenarioId: number) => ({
    type: 'REMOVE_SCENARIO' as const,
    payload: scenarioId,
  }),

  clearScenarios: () => ({
    type: 'CLEAR_SCENARIOS' as const,
  }),

  setSelectedScenarios: (scenarioIds: number[]) => ({
    type: 'SET_SELECTED_SCENARIOS' as const,
    payload: scenarioIds,
  }),

  toggleScenarioSelection: (scenarioId: number) => ({
    type: 'TOGGLE_SCENARIO_SELECTION' as const,
    payload: scenarioId,
  }),

  clearAll: () => ({
    type: 'CLEAR_ALL' as const,
  }),

  initGenerationSession: (totalCategories: number) => ({
    type: 'INIT_GENERATION_SESSION' as const,
    payload: { totalCategories },
  }),

  incrementGenerationCompleted: () => ({
    type: 'INCREMENT_GENERATION_COMPLETED' as const,
  }),

  endGenerationSession: () => ({
    type: 'END_GENERATION_SESSION' as const,
  }),

  setPinnedAlternateScenarioIds: (ids: number[]) => ({
    type: 'SET_PINNED_ALTERNATE_SCENARIO_IDS' as const,
    payload: ids,
  }),

  mergeAlternateParentMap: (map: Record<number, number>) => ({
    type: 'MERGE_ALTERNATE_PARENT_MAP' as const,
    payload: map,
  }),

  setExcludedPayerInfo: (category: string, excludedPayers: string[], excludedPayerVolume: number) => ({
    type: 'SET_EXCLUDED_PAYER_INFO' as const,
    payload: { category, excludedPayers, excludedPayerVolume },
  }),

  setMissingFeeSchedulePfIds: (category: string, pfIds: string[]) => ({
    type: 'SET_MISSING_FEE_SCHEDULE_PF_IDS' as const,
    payload: { category, pfIds },
  }),

  setFeeScheduleGaps: (category: string, gaps: Record<string, FeeScheduleGap>) => ({
    type: 'SET_FEE_SCHEDULE_GAPS' as const,
    payload: { category, gaps },
  }),

  mergePayerProfitability: (byScenarioId: Record<number, Record<string, PayerProfitabilityItem>>) => ({
    type: 'MERGE_PAYER_PROFITABILITY' as const,
    payload: byScenarioId,
  }),

  mergeRebateDetails: (details: RebateDetail[]) => ({
    type: 'MERGE_REBATE_DETAILS' as const,
    payload: details,
  }),
};
